import React, { useCallback, useLayoutEffect, useState } from 'react';
import Animated from 'react-native-reanimated';
import { RRule } from 'rrule';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { AppView, Button, CardView, SettingsItemWithCheckBox } from 'design-system';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Text from 'design-system/Text';
import { SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { savePotsOnboardingState } from 'features/pots/actions';
import useNavigateToNextScreenForPotsOnboarding from 'features/pots/hooks/useNavigateToNextScreenForPotsOnboarding';
import { selectAutosaveCollectionFrequency } from 'features/pots/reducers/selectors';
const AutosaveChoosePeriod = withAnimatedNavigationHeader(({ scrollHandler, navigation }) => {
    const dispatch = useAppDispatch();
    const collectionFrequency = useAppSelector(selectAutosaveCollectionFrequency);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const hasOnboardingState = useAppSelector((state) => !!state.pots.onboarding);
    const navigateToNextScreen = useNavigateToNextScreenForPotsOnboarding();
    const [state, setState] = useState(!collectionFrequency || collectionFrequency === 'WEEKLY' ? 'WEEKLY' : 'MONTHLY');
    const hasChangedCollection = collectionFrequency !== state;
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton articlesKey={HELP_ARTICLES_KEY.AUTOSAVE_ONBOARDING}/>,
        });
    }, []);
    const onSelectPeriod = useCallback((period) => () => {
        setState(period);
    }, []);
    const handleContinue = useCallback(() => {
        if (!hasChangedCollection) {
            navigation.pop();
            return;
        }
        if (state === 'WEEKLY') {
            const newCollectionFrequency = {
                rrule: new RRule({
                    freq: RRule.WEEKLY,
                    byweekday: RRule.WE,
                }).toString(),
            };
            if (hasOnboardingState) {
                dispatch(savePotsOnboardingState({ collectionFrequency: newCollectionFrequency }));
                navigateToNextScreen();
                return;
            }
            navigation.navigate('AutosaveChooseSavingRules', {
                collectionFrequency: newCollectionFrequency,
            });
        }
        else {
            navigation.navigate('AutosaveChooseMonthlyFrequency');
        }
    }, [hasChangedCollection, hasOnboardingState, navigateToNextScreen, state]);
    return (<AppView type="secondary" withFrame={false}>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader title="When would you like the savings to be collected?" subTitle="Decide how often you’d like to save automatically"/>
          <CardView noMarginTop>
            <SettingsItemWithCheckBox topDescription={<Text Text-12 Primary>
                  RECOMMENDED
                </Text>} title="Weekly" selected={state === 'WEEKLY'} description="Executed every week" onPress={onSelectPeriod('WEEKLY')}/>
            <SettingsItemWithCheckBox title="Monthly" selected={state === 'MONTHLY'} description="Executed every month" onPress={onSelectPeriod('MONTHLY')}/>
          </CardView>
        </Animated.ScrollView>
        <Button brand floating title={hasChangedCollection ? 'Continue' : 'Save'} onPress={handleContinue}/>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
export default AutosaveChoosePeriod;
